<template>
  <section
    id="overlay-drawer"
    ref="overlayDrawer"
    :style="{...overlayDrawerStyle}"
    :class="{ 'overlay-drawer--extended': drawerExtended, 'overlay-drawer': !drawerExtended}"
    class="
      $color/background:white
      color/background
      layout/fixed
      layout/padding:bottom:page
      layout/padding:top:page
      layout/z-index:10
    "
  >
    <div
      class="
        @600/layout/padding:right:page:clear
        layout/padding:right:page
      "
      >
      <div class="layout/relative">
        <OverlayCloseButton @close="hideDrawer" />
      </div>
    </div>
    <div
      ref="overlayDrawerContentContainer"
      class="
        layout/flex
        layout/justify:end
        layout/padding:bottom
      "
      >
      <OverlayDrawerContent
        :overlay-data="overlayData"
        :gallery-works="galleryWorks"
        :artists="artists"
      />
    </div>
  </section>
</template>

<script>
  import OverlayCloseButton from "./overlay-drawer-close-button.vue";
  import OverlayDrawerContent from "./overlay-drawer-content.vue";

  export default {
    components: {
    OverlayCloseButton,
    OverlayDrawerContent
},
    props: {
      hideDrawer: {
        type: Function,
        required: true
      },
      overlayData: {
        type: Object,
        required: false,
        default: () => ({})
      },
      isSingleColumnLayout: {
        type: Boolean,
        required: true
      }
    },
    data() {
      return {
        drawerExtended: this.setDrawerState(),
        isTransitioning: false,
        drawerLocked: false,
        touchStartY: 0,
        touchEndY: 0,
        touchStartX: 0,
        touchEndX: 0,
        overlayWidthStyle: {
          left: '0',
          right: '0',
          bottom: '0'
        },
        galleryWorks: [],
        artists: [],
      };
    },
    computed: {
      overlayDrawerStyle() {
        const boorderStyle = this.isSingleColumnLayout ?
        {
          borderTop: '2px solid black'
        } :
        {
          borderRight: '2px solid black'
        };
        return {
            ...boorderStyle,
            ...this.overlayWidthStyle,
          };
      },
    },
    watch: {
      overlayData: {
        immediate: true,
        async handler(newVal) {
          if (newVal.isGallery) {
            try {
              await this.fetchGalleryWorks(newVal.id);
            } catch (error) {
              console.error('Error fetching data:', error);
              this.galleryWorks = [];
            }
          }
        }
      }
    },
    async mounted() {
      window.addEventListener('resize', this.handleResize);
      this.$el.addEventListener('wheel', this.handleWheelScroll);
      this.$refs.overlayDrawerContentContainer.addEventListener('touchstart', this.handleTouchStart);
      this.$refs.overlayDrawerContentContainer.addEventListener('touchmove', this.handleTouchMove);
      this.$refs.overlayDrawerContentContainer.addEventListener('touchend', this.handleTouchEnd);

      this.updateOverlaySize();
      this.drawerLocked = !this.isSingleColumnLayout;
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
      this.$el.removeEventListener('wheel', this.handleWheelScroll);
      this.$refs.overlayDrawerContentContainer.removeEventListener('touchstart', this.handleTouchStart);
      this.$refs.overlayDrawerContentContainer.removeEventListener('touchmove', this.handleTouchMove);
      this.$refs.overlayDrawerContentContainer.removeEventListener('touchend', this.handleTouchEnd);
    },
    methods: {
      setDrawerState() {
        return !this.isSingleColumnLayout;
      },
      handleWheelScroll(e) {
        if (Math.abs(e.deltaX) >= Math.abs(e.deltaY) || this.drawerLocked || this.isTransitioning) return;
        if (e.deltaY > 0 && !this.drawerExtended) {
          this.expandDrawer();
        } else if (e.deltaY < 0 && this.drawerExtended && this.$refs.overlayDrawer.scrollTop === 0) {
          this.collapseDrawer();
        }
      },
      handleTouchStart(event) {
        if (this.isTransitioning) return;
        this.touchStartY = event.touches[0].clientY;
        this.touchStartX = event.touches[0].clientX;
      },
      handleTouchMove(event) {
        if (this.isTransitioning) return;
        this.touchEndY = event.touches[0].clientY;
        this.touchEndX = event.touches[0].clientX;
      },
      handleTouchEnd() {
        if (this.shouldIgnoreEvent()) return;

        const swipedUp = this.isSwipedUp();
        const atTopOfScrollableContent = this.$refs.overlayDrawer.scrollTop === 0;

        if (swipedUp && !this.drawerExtended) {
          this.expandDrawer();
        } else if (!swipedUp && this.drawerExtended && atTopOfScrollableContent) {
          this.collapseDrawer();
        }
      },
      shouldIgnoreEvent() {
        const deltaX = Math.abs(this.touchEndX - this.touchStartX);
        const deltaY = this.touchEndY - this.touchStartY;
        return deltaX > Math.abs(deltaY) || this.isTransitioning || this.drawerLocked;
      },
      isSwipedUp() {
        const deltaY = this.touchEndY - this.touchStartY;
        return deltaY < 0;
      },
      expandDrawer() {
        this.isTransitioning = true;
        this.drawerExtended = true;
        this.$refs.overlayDrawerContentContainer.scrollTop = 0;
        setTimeout(() => {
          this.isTransitioning = false;
        }, 500);
      },
      collapseDrawer() {
        this.isTransitioning = true;
        this.drawerExtended = false;
        this.$refs.overlayDrawerContentContainer.scrollTop = 0;
        setTimeout(() => {
          this.isTransitioning = false;
        }, 500);
      },
      updateOverlaySize() {
        const container = document.getElementById('visit-guide-map-exhibitions');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          this.overlayWidthStyle = {
            left: '0',
            right: `${document.documentElement.clientWidth - containerRect.right}px`,
            bottom: '0'
          };
        }

        const bottomOfMap = document.querySelector('#map').getBoundingClientRect().bottom;
        this.$el.style.setProperty('--bottom-of-map', `${bottomOfMap}px`);
      },
      handleResize() {
        this.updateOverlaySize();
      },
      async fetchGalleryWorks(id) {
        const res = await fetch(`/calendar/galleries/${id}/artworks.json`);
        if (!res.ok) {
          throw new Error('Failed to fetch gallery artworks data');
        }

        const data = await res.json();
        this.galleryWorks = data.ArtWorks;
        this.artists = data.Artists;
      }
    }
  };
</script>

<style>
  #overlay-drawer {
    --animation-speed: 0.5s;
  }
  .overlay-drawer--extended {
    transition: top var(--animation-speed);
    top: var(--header-height--current);
    overflow-y: auto;
  }
  
  .overlay-drawer {
    transition: top var(--animation-speed);
    top: var(--bottom-of-map);
    overflow-y: hidden;
  }
</style>